import { useEffect, useState } from "react";
import { CheckCircle, ExclamationTriangle, InfoCircle, X, XCircle } from "react-bootstrap-icons";
import { alertService } from "services";

// Shows alertService messages (success/error/info/warn) as design-system
// toasts at the bottom right, instead of alert boxes inside each page.
type Toast = { key: number; type: string; message: string; leaving?: boolean };

const VARIANT: Record<string, { cls: string; title: string; Icon: typeof X }> = {
  Success: { cls: "toast-success", title: "Sucesso", Icon: CheckCircle },
  Error: { cls: "toast-error", title: "Erro", Icon: XCircle },
  Info: { cls: "toast-info", title: "Informação", Icon: InfoCircle },
  Warning: { cls: "toast-warning", title: "Atenção", Icon: ExclamationTriangle },
};

const AUTO_CLOSE_MS = 6000;
const EXIT_MS = 200; // = .toast-out animation (--duration-200) in bridge.css

// Errors from fetch/handleResponse arrive as strings or Error-like objects
const toText = (m: any) => (typeof m === "string" ? m : m?.message ?? String(m));

const ToastContainer = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Play the exit animation, then remove
  const dismiss = (key: number) => {
    setToasts((ts) => ts.map((t) => (t.key === key ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((ts) => ts.filter((t) => t.key !== key)), EXIT_MS);
  };

  useEffect(() => {
    let nextKey = 0;
    const sub = alertService.onAlert().subscribe((alert: any) => {
      if (!alert.message) return setToasts([]); // alertService.clear()
      const toast = { key: ++nextKey, type: alert.type, message: toText(alert.message) };
      setToasts((ts) => [...ts, toast]);
      if (alert.autoClose) setTimeout(() => dismiss(toast.key), AUTO_CLOSE_MS);
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="toast-container" role="status" aria-live="polite">
      {toasts.map(({ key, type, message, leaving }) => {
        const { cls, title, Icon } = VARIANT[type] || VARIANT.Info;
        return (
          <div key={key} className={`toast ${cls}${leaving ? " toast-out" : ""}`}>
            <Icon className="toast-icon" />
            <div className="toast-content">
              <div className="toast-title">{title}</div>
              <div className="toast-description">{message}</div>
            </div>
            <button
              type="button"
              className="toast-close"
              aria-label="Fechar"
              onClick={() => dismiss(key)}
            >
              <X />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ToastContainer;
